import React, { useEffect, useState } from 'react';
import { orgAPI } from '@/services/api';
import { Users, UserPlus, Check, X, ShieldOff, ShieldCheck, Crown, Mail, Clock, AlertCircle } from 'lucide-react';
import Pagination, { usePagination } from '@/components/shared/Pagination';

const STATUS_LABEL = {
  active: { label: 'Active', cls: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10' },
  pending_admin: { label: 'Pending Numuv approval', cls: 'text-amber-600 dark:text-amber-400 bg-amber-500/10' },
  pending_head: { label: 'Pending head approval', cls: 'text-amber-600 dark:text-amber-400 bg-amber-500/10' },
  revoked: { label: 'Access revoked', cls: 'text-red-600 dark:text-red-400 bg-red-500/10' },
  rejected: { label: 'Rejected', cls: 'text-red-600 dark:text-red-400 bg-red-500/10' },
};

function StatusPill({ status }) {
  const s = STATUS_LABEL[status] || { label: status, cls: 'text-slate-500 dark:text-zinc-400 bg-zinc-500/10' };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>;
}

export default function RecruiterTeam() {
  const [ctx, setCtx] = useState(null);
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', position: 'Recruiter' });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const membersPg = usePagination(members, 20);

  const refresh = async () => {
    try {
      const c = await orgAPI.getContext();
      setCtx(c);
      const m = await orgAPI.getMembers();
      setMembers(m.members || []);
      if (c.is_head) {
        try {
          const p = await orgAPI.getPending();
          setPending(p.pending || []);
        } catch {/* ignore */}
        try {
          const inv = await orgAPI.listInvitations();
          setInvitations(inv.invitations || []);
        } catch {/* ignore */}
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to load' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3500); };

  const handle = async (fn, success) => {
    try { await fn(); flash('success', success); refresh(); }
    catch (e) { flash('error', e.response?.data?.detail || e.message); }
  };

  const invite = async (e) => {
    e.preventDefault();
    try {
      const r = await orgAPI.invite(inviteForm.email.trim(), inviteForm.name, inviteForm.position);
      flash('success', `Invite sent to ${inviteForm.email}`);
      setShowInvite(false);
      setInviteForm({ email: '', name: '', position: 'Recruiter' });
      refresh();
    } catch (e) {
      flash('error', e.response?.data?.detail || e.message);
    }
  };

  if (loading) return <div className="text-slate-500 dark:text-zinc-400 text-sm" data-testid="recruiter-team-loading">Loading team…</div>;
  if (!ctx?.has_profile) {
    return <div className="text-slate-500 dark:text-zinc-400 text-sm" data-testid="recruiter-team-no-profile">Complete your recruiter profile to view your team.</div>;
  }

  const isHead = ctx.is_head;
  const orgName = ctx.organization?.name || 'Your organization';
  const domain = ctx.organization?.domain || '';
  const orgPending = !ctx.org_approved;
  const orgRevoked = ctx.domain_status === 'revoked';

  return (
    <div data-testid="recruiter-team">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight">{orgName}</h1>
            {isHead && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 font-medium" data-testid="head-badge"><Crown className="w-3 h-3" /> Recruiter Head</span>}
          </div>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">@{domain} · {members.length} member{members.length !== 1 && 's'}</p>
        </div>
        {isHead && !orgRevoked && !orgPending && (
          <button onClick={() => setShowInvite(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-zinc-100 transition" data-testid="invite-btn">
            <UserPlus className="w-4 h-4" /> Invite recruiter
          </button>
        )}
      </div>

      {/* Banners */}
      {orgRevoked && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 text-sm flex items-start gap-2" data-testid="banner-domain-revoked">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>Your organization&apos;s domain access has been revoked by Numuv admin. Posting and team operations are disabled.</div>
        </div>
      )}
      {!orgRevoked && orgPending && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-sm flex items-start gap-2" data-testid="banner-org-pending">
          <Clock className="w-4 h-4 mt-0.5 shrink-0" />
          <div>Your organization is awaiting Numuv admin approval. You&apos;ll be able to post jobs and invite recruiters once approved.</div>
        </div>
      )}
      {msg && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${msg.type === 'error' ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`} data-testid="team-msg">{msg.text}</div>
      )}

      {/* Pending join requests (head only) */}
      {isHead && pending.length > 0 && (
        <section className="mb-6" data-testid="pending-section">
          <h2 className="text-sm font-semibold text-slate-600 dark:text-zinc-300 mb-2 uppercase tracking-wider">Pending join requests · {pending.length}</h2>
          <div className="space-y-2">
            {pending.map(p => (
              <div key={p.user_id} className="bg-white dark:bg-[#111114] border border-amber-500/30 rounded-lg p-4 flex items-center justify-between" data-testid={`pending-${p.user_id}`}>
                <div>
                  <p className="text-slate-900 dark:text-white font-medium">{p.name || p.email.split('@')[0]}</p>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs">{p.email} · {p.position}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handle(() => orgAPI.approve(p.user_id), 'Member approved')} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-medium" data-testid={`approve-${p.user_id}`}><Check className="w-3 h-3" /> Approve</button>
                  <button onClick={() => handle(() => orgAPI.reject(p.user_id), 'Request rejected')} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 rounded-lg text-xs font-medium" data-testid={`reject-${p.user_id}`}><X className="w-3 h-3" /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Members list */}
      <section data-testid="members-section">
        <h2 className="text-sm font-semibold text-slate-600 dark:text-zinc-300 mb-2 uppercase tracking-wider flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Members</h2>
        <div className="space-y-2">
          {membersPg.pageItems.map(m => (
            <div key={m.user_id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 flex items-center justify-between" data-testid={`member-${m.user_id}`}>
              <div className="flex items-center gap-3">
                {m.is_head && <Crown className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />}
                <div>
                  <p className="text-slate-900 dark:text-white font-medium flex items-center gap-2">{m.name || m.email.split('@')[0]} {m.is_head && <span className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300">Head</span>}</p>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs">{m.email} · {m.position}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusPill status={m.member_status} />
                {isHead && !m.is_head && (
                  m.member_status === 'revoked' ? (
                    <button onClick={() => handle(() => orgAPI.restore(m.user_id), 'Member restored')} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-medium" data-testid={`restore-${m.user_id}`}><ShieldCheck className="w-3 h-3" /> Restore</button>
                  ) : m.member_status === 'active' ? (
                    <button onClick={() => handle(() => orgAPI.revoke(m.user_id), 'Member access revoked')} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 rounded-lg text-xs font-medium" data-testid={`revoke-${m.user_id}`}><ShieldOff className="w-3 h-3" /> Revoke</button>
                  ) : null
                )}
              </div>
            </div>
          ))}
        </div>
        <Pagination {...membersPg.paginationProps} testIdPrefix="recruiter-team-pg" />
      </section>
      {isHead && invitations.length > 0 && (
        <section className="mt-6" data-testid="invitations-section">
          <h2 className="text-sm font-semibold text-slate-600 dark:text-zinc-300 mb-2 uppercase tracking-wider flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Invitations</h2>
          <div className="space-y-2">
            {invitations.slice(0, 10).map(i => (
              <div key={i.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-3 flex items-center justify-between text-sm">
                <div>
                  <span className="text-slate-900 dark:text-white">{i.email}</span>
                  <span className="text-slate-500 dark:text-zinc-600 ml-2 text-xs">{i.status}</span>
                </div>
                {i.status === 'pending' && (
                  <button onClick={() => handle(() => orgAPI.cancelInvitation(i.id), 'Invitation cancelled')} className="text-slate-500 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400 text-xs" data-testid={`cancel-invite-${i.id}`}>Cancel</button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowInvite(false)} data-testid="invite-modal">
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Invite a recruiter</h2>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mb-4">Email must use your organization domain: <span className="text-amber-700 dark:text-amber-300">@{domain}</span></p>
            <form onSubmit={invite} className="space-y-3">
              <input type="email" placeholder={`name@${domain}`} value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} required className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" data-testid="invite-email-input" />
              <input placeholder="Name (optional)" value={inviteForm.name} onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" data-testid="invite-name-input" />
              <input placeholder="Position" value={inviteForm.position} onChange={e => setInviteForm({ ...inviteForm, position: e.target.value })} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" data-testid="invite-position-input" />
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowInvite(false)} className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="flex-1 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium" data-testid="send-invite-btn">Send invite</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
